"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { GetSearchParametersRequest } from "@/app/shared/apis/top-deals";
import { useTopDealsClient } from "@/app/shared/config/api-clients";
import { SearchFiltersView, ActiveFiltersView } from "../models/filters";
import { LISTING_ROUTES } from "../constants/routes";
import {
	LISTING_FILTER_PARAM_KEYS,
	applyFilterParams,
} from "../utils/filter-url-params";
import { useFeatureFlag } from "@/app/shared/hooks/use-feature-flag";
import { TOP_DEALS_SORT_BY_MARGIN_FLAG } from "@/app/shared/constants/feature-flags";

export function useFilters(marketCountryCode: string, countryCodes?: string[]) {
	const topDealsClient = useTopDealsClient();
	const searchParams = useSearchParams();
	const router = useRouter();
	const pathname = usePathname();
	const [isLoading, setIsLoading] = useState(false);
	const [filters, setFilters] = useState<SearchFiltersView | null>(null);
	// Initialize activeFilters from URL params if they exist
	const [activeFilters, setActiveFilters] = useState(() => {
		if (searchParams.toString()) {
			return ActiveFiltersView.fromQueryParams(searchParams);
		}
		return new ActiveFiltersView();
	});
	const [error, setError] = useState<Error | null>(null);

	// Ref to track sync operations and prevent infinite loops
	const isSyncingToUrlRef = useRef(false);
	const isSyncingFromUrlRef = useRef(false);
	// Track last synced values to detect actual changes
	const lastUrlParamsRef = useRef<string>("");
	const lastActiveFiltersRef = useRef<string>("");

	// Fetch filters from the API
	const fetchFilters = useCallback(async () => {
		if (!topDealsClient) return;
		setIsLoading(true);
		setError(null);

		try {
			// Create request body - for initial load we may not need query parameters
			const requestBody: GetSearchParametersRequest = {
				countryCode: marketCountryCode,
				filters: null, // No filters for initial load
				...(countryCodes ? { countryCodes } : {}),
			};

			const response = await topDealsClient.dealerTopDeals.getSearchParameters({
				requestBody,
			});

			// Transform the API response into our view model
			const filtersViewModel = new SearchFiltersView(response);
			setFilters(filtersViewModel);

			return filtersViewModel;
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : "Failed to fetch filters";
			setError(new Error(errorMessage));
			throw err;
		} finally {
			setIsLoading(false);
		}
	}, [topDealsClient, marketCountryCode, countryCodes]);

	// Refresh filters based on current active filters
	const refreshFilters = useCallback(async () => {
		if (!topDealsClient) return;
		setIsLoading(true);
		setError(null);

		try {
			// Get current filter values
			const filterParams = activeFilters.toQueryParams();

			// Create request body including current filters
			const requestBody: GetSearchParametersRequest = {
				countryCode: marketCountryCode,
				filters: JSON.stringify(filterParams), // Convert filter params to JSON string
				...(countryCodes ? { countryCodes } : {}),
			};

			const response = await topDealsClient.dealerTopDeals.getSearchParameters({
				requestBody,
			});

			// Transform the API response into our view model
			const filtersViewModel = new SearchFiltersView(response);
			setFilters(filtersViewModel);

			return filtersViewModel;
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : "Failed to refresh filters";
			setError(new Error(errorMessage));
			throw err;
		} finally {
			setIsLoading(false);
		}
	}, [topDealsClient, marketCountryCode, activeFilters, countryCodes]);

	// Update active filters
	const updateFilters = useCallback(
		(
			newFilters:
				| Partial<ActiveFiltersView>
				| ((prev: ActiveFiltersView) => ActiveFiltersView)
		) => {
			if (typeof newFilters === "function") {
				setActiveFilters(newFilters);
			} else {
				setActiveFilters(prevFilters => {
					const updatedFilters = new ActiveFiltersView();

					// Copy all existing filters
					updatedFilters.setMakes(prevFilters.makes);
					updatedFilters.setModels(prevFilters.models);
					updatedFilters.setTrims(prevFilters.trims);
					updatedFilters.setSuppliers(prevFilters.suppliers);
					updatedFilters.setFuelTypes(prevFilters.fuelTypes);
					updatedFilters.setTransmissions(prevFilters.transmissions);
					updatedFilters.setSaleTypes(prevFilters.saleTypes);
					updatedFilters.setColors(prevFilters.colors);
					updatedFilters.setDoors(prevFilters.doors);
					updatedFilters.setSeats(prevFilters.seats);
					updatedFilters.setMarkets(prevFilters.markets);
					updatedFilters.setYearRange(prevFilters.minYear, prevFilters.maxYear);
					updatedFilters.setPriceRange(
						prevFilters.minPrice,
						prevFilters.maxPrice
					);
					updatedFilters.setSellPriceRange(
						prevFilters.minSellPrice,
						prevFilters.maxSellPrice
					);
					updatedFilters.setMileageRange(
						prevFilters.minMileage,
						prevFilters.maxMileage
					);
					updatedFilters.setEnginePowerRange(
						prevFilters.minEnginePower,
						prevFilters.maxEnginePower
					);
					updatedFilters.setCo2EmissionRange(
						prevFilters.minCo2Emission,
						prevFilters.maxCo2Emission
					);
					updatedFilters.setOnlyFavorites(prevFilters.onlyFavorites);
					updatedFilters.setHasInspectionReport(
						prevFilters.hasInspectionReport
					);
					updatedFilters.setHideDamaged(prevFilters.hideDamaged);
					updatedFilters.setHideNonOperable(prevFilters.hideNonOperable);
					updatedFilters.setOnlyDamaged(prevFilters.onlyDamaged);
					updatedFilters.setOnlyNonOperable(prevFilters.onlyNonOperable);
					updatedFilters.setAppraisalStatus(prevFilters.appraisalStatus);
					updatedFilters.setIsBrandNew(prevFilters.isBrandNew);
					updatedFilters.setSearchText(prevFilters.searchText);
					updatedFilters.setConditionGrades(prevFilters.conditionGrades);
					updatedFilters.setIncludeUngraded(prevFilters.includeUngraded);

					// Update with new filter values if provided
					if ("makes" in newFilters)
						updatedFilters.setMakes(newFilters.makes || []);
					if ("models" in newFilters)
						updatedFilters.setModels(newFilters.models || []);
					if ("trims" in newFilters)
						updatedFilters.setTrims(newFilters.trims || []);
					if ("suppliers" in newFilters) {
						updatedFilters.setSuppliers(newFilters.suppliers || []);
						// Turn off "New cars" toggle when user manually selects suppliers,
						// since the toggle overrides suppliers with Neuwagenkontor
						if (
							newFilters.suppliers &&
							newFilters.suppliers.length > 0 &&
							!("isBrandNew" in newFilters)
						) {
							updatedFilters.setIsBrandNew(false);
						}
					}
					if ("fuelTypes" in newFilters)
						updatedFilters.setFuelTypes(newFilters.fuelTypes || []);
					if ("transmissions" in newFilters)
						updatedFilters.setTransmissions(newFilters.transmissions || []);
					if ("saleTypes" in newFilters)
						updatedFilters.setSaleTypes(newFilters.saleTypes || []);
					if ("colors" in newFilters)
						updatedFilters.setColors(newFilters.colors || []);
					if ("doors" in newFilters)
						updatedFilters.setDoors(newFilters.doors || []);
					if ("seats" in newFilters)
						updatedFilters.setSeats(newFilters.seats || []);
					if ("markets" in newFilters)
						updatedFilters.setMarkets(newFilters.markets || []);

					// Handle range filters
					if ("minYear" in newFilters || "maxYear" in newFilters) {
						updatedFilters.setYearRange(
							"minYear" in newFilters
								? newFilters.minYear
								: prevFilters.minYear,
							"maxYear" in newFilters ? newFilters.maxYear : prevFilters.maxYear
						);
					}

					if ("minPrice" in newFilters || "maxPrice" in newFilters) {
						updatedFilters.setPriceRange(
							"minPrice" in newFilters
								? newFilters.minPrice
								: prevFilters.minPrice,
							"maxPrice" in newFilters
								? newFilters.maxPrice
								: prevFilters.maxPrice
						);
					}

					if ("minSellPrice" in newFilters || "maxSellPrice" in newFilters) {
						updatedFilters.setSellPriceRange(
							"minSellPrice" in newFilters
								? newFilters.minSellPrice
								: prevFilters.minSellPrice,
							"maxSellPrice" in newFilters
								? newFilters.maxSellPrice
								: prevFilters.maxSellPrice
						);
					}

					if ("minMileage" in newFilters || "maxMileage" in newFilters) {
						updatedFilters.setMileageRange(
							"minMileage" in newFilters
								? newFilters.minMileage
								: prevFilters.minMileage,
							"maxMileage" in newFilters
								? newFilters.maxMileage
								: prevFilters.maxMileage
						);
					}

					if (
						"minEnginePower" in newFilters ||
						"maxEnginePower" in newFilters
					) {
						updatedFilters.setEnginePowerRange(
							"minEnginePower" in newFilters
								? newFilters.minEnginePower
								: prevFilters.minEnginePower,
							"maxEnginePower" in newFilters
								? newFilters.maxEnginePower
								: prevFilters.maxEnginePower
						);
					}

					if (
						"minCo2Emission" in newFilters ||
						"maxCo2Emission" in newFilters
					) {
						updatedFilters.setCo2EmissionRange(
							"minCo2Emission" in newFilters
								? newFilters.minCo2Emission
								: prevFilters.minCo2Emission,
							"maxCo2Emission" in newFilters
								? newFilters.maxCo2Emission
								: prevFilters.maxCo2Emission
						);
					}

					if ("onlyFavorites" in newFilters) {
						updatedFilters.setOnlyFavorites(newFilters.onlyFavorites || false);
					}

					if ("hasInspectionReport" in newFilters) {
						updatedFilters.setHasInspectionReport(
							newFilters.hasInspectionReport
						);
					}

					if ("hideDamaged" in newFilters) {
						updatedFilters.setHideDamaged(newFilters.hideDamaged);
					}

					if ("hideNonOperable" in newFilters) {
						updatedFilters.setHideNonOperable(newFilters.hideNonOperable);
					}

					if ("onlyDamaged" in newFilters) {
						updatedFilters.setOnlyDamaged(newFilters.onlyDamaged);
					}

					if ("onlyNonOperable" in newFilters) {
						updatedFilters.setOnlyNonOperable(newFilters.onlyNonOperable);
					}

					if ("appraisalStatus" in newFilters) {
						updatedFilters.setAppraisalStatus(newFilters.appraisalStatus);
					}

					if ("isBrandNew" in newFilters) {
						updatedFilters.setIsBrandNew(newFilters.isBrandNew || false);
					}

					if ("searchText" in newFilters) {
						updatedFilters.setSearchText(newFilters.searchText);
					}

					if ("conditionGrades" in newFilters) {
						updatedFilters.setConditionGrades(newFilters.conditionGrades || []);
					}
					if ("includeUngraded" in newFilters) {
						updatedFilters.setIncludeUngraded(
							newFilters.includeUngraded || false
						);
					}

					// V2 location filter (plan §2.4). Copy the previous state first so
					// that unrelated updates don't wipe location selection.
					updatedFilters.setRegionCodes(prevFilters.regionCodes);
					updatedFilters.setProvinceCodes(prevFilters.provinceCodes);
					if (
						prevFilters.centerLat !== undefined &&
						prevFilters.centerLng !== undefined &&
						prevFilters.radiusKm !== undefined
					) {
						updatedFilters.setRadius(
							prevFilters.centerLat,
							prevFilters.centerLng,
							prevFilters.radiusKm,
							prevFilters.centerLabel
						);
					}
					if ("regionCodes" in newFilters) {
						updatedFilters.setRegionCodes(newFilters.regionCodes || []);
					}
					if ("provinceCodes" in newFilters) {
						updatedFilters.setProvinceCodes(newFilters.provinceCodes || []);
					}
					if (
						"centerLat" in newFilters ||
						"centerLng" in newFilters ||
						"radiusKm" in newFilters
					) {
						const lat =
							"centerLat" in newFilters
								? newFilters.centerLat
								: prevFilters.centerLat;
						const lng =
							"centerLng" in newFilters
								? newFilters.centerLng
								: prevFilters.centerLng;
						const radius =
							"radiusKm" in newFilters
								? newFilters.radiusKm
								: prevFilters.radiusKm;
						if (
							lat !== undefined &&
							lng !== undefined &&
							radius !== undefined
						) {
							const label =
								"centerLabel" in newFilters
									? newFilters.centerLabel
									: prevFilters.centerLabel;
							updatedFilters.setRadius(lat, lng, radius, label);
						} else {
							updatedFilters.clearLocationFilter();
						}
					}

					return updatedFilters;
				});
			}
		},
		[]
	);

	// The flag only hides the "Sell for" control; clear a range that came in through the URL too.
	const { isEnabled: isSellPriceEnabled, isLoading: isSellPriceFlagLoading } =
		useFeatureFlag(TOP_DEALS_SORT_BY_MARGIN_FLAG);
	const hasSellPriceRange =
		activeFilters.minSellPrice !== undefined ||
		activeFilters.maxSellPrice !== undefined;
	useEffect(() => {
		if (!isSellPriceFlagLoading && !isSellPriceEnabled && hasSellPriceRange) {
			updateFilters({ minSellPrice: undefined, maxSellPrice: undefined });
		}
	}, [
		isSellPriceFlagLoading,
		isSellPriceEnabled,
		hasSellPriceRange,
		updateFilters,
	]);

	// Reset all filters
	const resetFilters = useCallback(() => {
		setActiveFilters(new ActiveFiltersView());
	}, []);

	// Initial fetch of filters when component mounts or country changes
	useEffect(() => {
		fetchFilters();
		// Only reset filters if there are no URL params
		// This allows saved search filters to persist on page load
		if (!searchParams.toString()) {
			setActiveFilters(prev =>
				pathname === LISTING_ROUTES.MAIN && prev.hasAppliedFilters()
					? prev
					: new ActiveFiltersView()
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fetchFilters, marketCountryCode]);
	// Note: intentionally excluding resetFilters and searchParams from deps to prevent reset on every render

	// Watch for URL param changes and update active filters
	// This handles navigation from saved searches or other sources with URL params
	useEffect(() => {
		// Only sync URL → filters on listing routes that can legitimately contain
		// filter query params. Detail pages (/listing/[dealId]) should remain
		// excluded because syncing there would incorrectly reset filter context.
		const shouldSyncFromUrl =
			pathname === LISTING_ROUTES.MAIN || pathname === LISTING_ROUTES.FILTERS;

		if (!shouldSyncFromUrl) {
			return;
		}

		// Skip if we're in the middle of syncing to URL (prevents loop)
		if (isSyncingToUrlRef.current) {
			return;
		}

		const urlParamsString = searchParams.toString();

		// Check if URL params actually changed to prevent unnecessary updates
		if (urlParamsString === lastUrlParamsRef.current) {
			return;
		}

		// Mark that we're syncing from URL
		isSyncingFromUrlRef.current = true;
		lastUrlParamsRef.current = urlParamsString;

		// Only update if URL params exist and are different from current active filters.
		// When the URL has no params (e.g. router.back() from /listing/filters lands on a
		// bare /listing), do NOT wipe in-memory filter state — the filters → URL effect
		// below is the single source of truth that pushes filter changes into the URL.
		// Wiping here would silently clear the user's selection on every back-navigation
		// from the mobile filters page.
		if (urlParamsString) {
			const filtersFromUrl = ActiveFiltersView.fromQueryParams(searchParams);
			const currentParamsString = JSON.stringify(activeFilters.toURLParams());
			const newParamsString = JSON.stringify(filtersFromUrl.toURLParams());

			// Only update if filters actually changed to avoid infinite loops
			if (currentParamsString !== newParamsString) {
				setActiveFilters(filtersFromUrl);
			}
		}

		// Reset sync flag after state updates complete (cleanup function)
		return () => {
			isSyncingFromUrlRef.current = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams, pathname]);
	// Note: intentionally excluding activeFilters and resetFilters to prevent infinite loops

	// Sync active filters to URL when they change
	// This ensures filters persist when navigating between pages
	useEffect(() => {
		// Only sync filters → URL on the main listing page.
		// On detail pages, filters page, or any other route, leave the URL alone.
		if (pathname !== LISTING_ROUTES.MAIN) {
			return;
		}

		// Skip if we're in the middle of syncing from URL (prevents loop)
		if (isSyncingFromUrlRef.current) {
			return;
		}

		const filterParams = activeFilters.toURLParams(); // Use toURLParams() for URL consistency
		const activeFiltersString = JSON.stringify(filterParams);

		// Check if active filters actually changed to prevent unnecessary updates
		if (activeFiltersString === lastActiveFiltersRef.current) {
			return;
		}

		// Start with existing URL params to preserve non-filter params like totalResults
		const params = new URLSearchParams(searchParams);

		LISTING_FILTER_PARAM_KEYS.forEach(key => params.delete(key));
		applyFilterParams(params, filterParams);

		const newParamsString = params.toString();
		const currentParamsString = searchParams.toString();

		// Only update URL if params actually changed
		if (newParamsString !== currentParamsString) {
			// Mark that we're syncing to URL
			isSyncingToUrlRef.current = true;
			lastActiveFiltersRef.current = activeFiltersString;

			const newUrl = newParamsString
				? `${pathname}?${newParamsString}`
				: pathname;
			router.replace(newUrl, { scroll: false });

			// Reset sync flag after navigation completes (cleanup function)
			return () => {
				isSyncingToUrlRef.current = false;
			};
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeFilters, pathname]);
	// Note: intentionally excluding router and searchParams to prevent infinite loops

	return {
		filters,
		activeFilters,
		isLoading,
		error,
		updateFilters,
		refreshFilters,
		resetFilters,
	};
}
